import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../_shared/hooks';
import { toCreativeSpec, toSubmit, type DraftCreative } from './draft';
import type { MetaDraft } from './draft-fixtures';
import {
  MetaAdImportSchema,
  MetaAdSearchResponseSchema,
  type MetaAdSearchResult,
  LinkedInCampaignDetailResponseSchema,
  LinkedInCampaignGroupListResponseSchema,
  LinkedInCampaignListResponseSchema,
  LinkedInPostResolveResponseSchema,
  LinkedInPublishResponseSchema,
  LinkedInStatusResponseSchema,
  MetaVideoSchema,
  type MetaVideoInfo,
  VIDEO_CHUNK_BYTES,
  VideoUploadOffsetsSchema,
  VideoUploadStartResponseSchema,
  AgentTurnResponseSchema,
  CREATIVE_DRAFT_HEADER,
  CampaignAskResponseSchema,
  CreativeCheckResponseSchema,
  AdRequestCreatedResponseSchema,
  AdRequestUpdatedResponseSchema,
  AdRequestListResponseSchema,
  AdRequestResponseSchema,
  MetaAdPreviewResponseSchema,
  MetaAudienceListResponseSchema,
  type MetaAudience,
  MetaLeadFormListResponseSchema,
  MetaLeadFormSpecResponseSchema,
  MetaCampaignDetailResponseSchema,
  MetaCampaignListResponseSchema,
  MetaPublishResponseSchema,
  MetaStatusResponseSchema,
  type AdRequest,
  type AdRequestDetail,
  type AdRequestUpdate,
  type CreativeSpec,
  type AdPlatform,
  type AgentTurnRequest,
  type ChatMessages,
  type LinkedInRequestSubmit,
} from './types';

const BASE = '/api/tools/ad-command-center';
type ApiFetch = ReturnType<typeof useApi>['apiFetch'];
const META_KEY = ['ad-command-center', 'meta'] as const;
const REQUESTS_KEY = ['ad-command-center', 'requests'] as const;
const LINKEDIN_KEY = ['ad-command-center', 'linkedin'] as const;

export function useAdRequests() {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: REQUESTS_KEY,
    queryFn: async (): Promise<AdRequest[]> => {
      const res = await apiFetch(`${BASE}/requests`);
      return AdRequestListResponseSchema.parse(await res.json()).requests;
    },
  });
}

export function useAdRequest(id: string) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...REQUESTS_KEY, id],
    queryFn: async (): Promise<AdRequestDetail> => {
      const res = await apiFetch(`${BASE}/requests/${encodeURIComponent(id)}`);
      return AdRequestResponseSchema.parse(await res.json()).request;
    },
  });
}

export function useMetaStatus() {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...META_KEY, 'status'],
    queryFn: async () => MetaStatusResponseSchema.parse(await (await apiFetch(`${BASE}/meta/status`)).json()),
    staleTime: Infinity,
  });
}

export function useMetaAudiences(enabled: boolean) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...META_KEY, 'audiences'],
    queryFn: async () =>
      MetaAudienceListResponseSchema.parse(await (await apiFetch(`${BASE}/meta/audiences`)).json()).audiences,
    enabled,
    staleTime: Infinity,
  });
}

export function useMetaLeadForms(enabled: boolean) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...META_KEY, 'lead-forms'],
    queryFn: async () =>
      MetaLeadFormListResponseSchema.parse(await (await apiFetch(`${BASE}/meta/lead-forms`)).json()).forms,
    enabled,
    staleTime: Infinity,
  });
}

/** Loads an existing form's content to duplicate it. */
export function useDuplicateLeadForm() {
  const { apiFetch } = useApi();
  return useMutation({
    mutationFn: async (formId: string) =>
      MetaLeadFormSpecResponseSchema.parse(await (await apiFetch(`${BASE}/meta/lead-forms/${encodeURIComponent(formId)}`)).json()).spec,
  });
}

export function useMetaCampaigns(enabled: boolean) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...META_KEY, 'campaigns'],
    queryFn: async () =>
      MetaCampaignListResponseSchema.parse(await (await apiFetch(`${BASE}/meta/campaigns`)).json()).campaigns,
    enabled,
  });
}

export function useMetaCampaign(id: string) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...META_KEY, 'campaigns', id],
    queryFn: async () =>
      MetaCampaignDetailResponseSchema.parse(
        await (await apiFetch(`${BASE}/meta/campaigns/${encodeURIComponent(id)}`)).json(),
      ),
  });
}

export function useMetaAdPreview(adId: string) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...META_KEY, 'ads', adId, 'preview'],
    queryFn: async () =>
      MetaAdPreviewResponseSchema.parse(await (await apiFetch(`${BASE}/meta/ads/${encodeURIComponent(adId)}/preview`)).json())
        .html,
    staleTime: Infinity,
  });
}

type Placement = 'feed' | 'vertical';
const creativeImageUrl = (requestId: string, creativeId: string, placement: Placement = 'feed') =>
  `${BASE}/requests/${encodeURIComponent(requestId)}/creatives/${encodeURIComponent(creativeId)}/${placement === 'vertical' ? 'vertical-image' : 'image'}`;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => (typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Unreadable image')));
    reader.onerror = () => reject(reader.error ?? new Error('Unreadable image'));
    reader.readAsDataURL(blob);
  });
}

/** Each creative's images as data URLs, keyed by creative id; missing until they load. */
export function useCreativeImages(
  requestId: string,
  creatives: { id: string; hasImage: boolean; hasVerticalImage: boolean }[],
) {
  const { apiFetch } = useApi();
  const wanted = creatives.flatMap(c => [
    ...(c.hasImage ? [{ id: c.id, placement: 'feed' as const }] : []),
    ...(c.hasVerticalImage ? [{ id: c.id, placement: 'vertical' as const }] : []),
  ]);
  const results = useQueries({
    queries: wanted.map(w => ({
      queryKey: [...REQUESTS_KEY, requestId, 'creatives', w.id, w.placement],
      queryFn: async () => blobToDataUrl(await (await apiFetch(creativeImageUrl(requestId, w.id, w.placement))).blob()),
      staleTime: Infinity,
    })),
  });
  const urls = { feed: new Map<string, string>(), vertical: new Map<string, string>() };
  wanted.forEach((w, i) => {
    const data = results[i]?.data;
    if (data) urls[w.placement].set(w.id, data);
  });
  return { urls, loading: results.some(r => r.isLoading) };
}

/** Uploads a video to the Meta ad account in the chunks Meta asks for; resolves with its video id. */
async function uploadVideo(apiFetch: ApiFetch, file: File, onProgress: (fraction: number) => void): Promise<string> {
  const json = { 'Content-Type': 'application/json' };
  const start = VideoUploadStartResponseSchema.parse(
    await (await apiFetch(`${BASE}/videos/start`, { method: 'POST', headers: json, body: JSON.stringify({ fileSize: file.size }) })).json(),
  );
  let { startOffset, endOffset } = start;
  while (startOffset < endOffset) {
    const chunk = file.slice(startOffset, Math.min(endOffset, startOffset + VIDEO_CHUNK_BYTES));
    const res = await apiFetch(`${BASE}/videos/${start.sessionId}/chunk?startOffset=${startOffset}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: chunk,
      timeoutMs: 120_000,
    });
    ({ startOffset, endOffset } = VideoUploadOffsetsSchema.parse(await res.json()));
    onProgress(Math.min(1, startOffset / file.size));
  }
  await apiFetch(`${BASE}/videos/${start.sessionId}/finish`, { method: 'POST', headers: json, body: JSON.stringify({ title: file.name.slice(0, 200) }) });
  return start.videoId;
}

function metaVideoQuery(apiFetch: ApiFetch, videoId: string | null) {
  return {
    queryKey: [...META_KEY, 'videos', videoId],
    queryFn: async () => MetaVideoSchema.parse(await (await apiFetch(`${BASE}/videos/${encodeURIComponent(videoId ?? '')}`)).json()),
    enabled: videoId !== null,
    // Meta transcodes after upload; keep checking until it settles.
    refetchInterval: (query: { state: { data?: MetaVideoInfo } }) => (query.state.data?.state === 'processing' ? 5000 : false),
  };
}

export function useMetaVideo(videoId: string | null) {
  const { apiFetch } = useApi();
  return useQuery(metaVideoQuery(apiFetch, videoId));
}

/** The videos' states, keyed by video id; one still loading counts as processing. */
export function useMetaVideoStates(videoIds: string[]) {
  const { apiFetch } = useApi();
  const results = useQueries({ queries: videoIds.map(id => metaVideoQuery(apiFetch, id)) });
  return new Map(videoIds.map((id, i) => [id, results[i]?.data?.state ?? 'processing']));
}

/** Uploads each newly picked video, main and vertical, then returns the creative specs to save. */
async function uploadCreativeVideos(
  apiFetch: ApiFetch,
  creatives: DraftCreative[],
  onVideoProgress: (fraction: number) => void,
): Promise<CreativeSpec[]> {
  const toUpload = creatives.flatMap(c => [c.media, c.vertical]).filter(m => m?.video?.file).length;
  let uploaded = 0;
  const upload = async (media: DraftCreative['media']) => {
    const video = media?.video;
    if (!video?.file) return video?.id ?? null;
    const done = uploaded;
    const id = await uploadVideo(apiFetch, video.file, f => onVideoProgress((done + f) / toUpload));
    uploaded++;
    return id;
  };
  const specs: CreativeSpec[] = [];
  for (const creative of creatives) {
    const videoId = await upload(creative.media);
    const verticalVideoId = await upload(creative.vertical);
    specs.push(toCreativeSpec(creative, videoId, verticalVideoId));
  }
  return specs;
}

/** Uploads the newly picked images of each creative; `ids` lines up with `creatives`. */
async function uploadCreativeImages(apiFetch: ApiFetch, requestId: string, creatives: DraftCreative[], ids: (string | null)[]) {
  const uploads = creatives.flatMap((creative, i) => {
    const id = ids[i];
    if (!id) return [];
    return [
      { id, placement: 'feed' as const, file: creative.media?.file },
      { id, placement: 'vertical' as const, file: creative.vertical?.file },
    ].flatMap(u => (u.file ? [{ ...u, file: u.file }] : []));
  });
  await Promise.all(
    uploads.map(({ id, placement, file }) =>
      apiFetch(creativeImageUrl(requestId, id, placement), { method: 'PUT', headers: { 'Content-Type': file.type }, body: file }),
    ),
  );
}

export function useSubmitAdRequest() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      draft,
      creatives,
      audiences,
      onVideoProgress,
    }: {
      draft: MetaDraft;
      creatives: DraftCreative[];
      /** The account's audiences, which Growth's default exclusions are resolved against. */
      audiences: MetaAudience[];
      onVideoProgress: (fraction: number) => void;
    }) => {
      const specs = await uploadCreativeVideos(apiFetch, creatives, onVideoProgress);
      const res = await apiFetch(`${BASE}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toSubmit(draft, specs, audiences)),
      });
      const { id, creativeIds } = AdRequestCreatedResponseSchema.parse(await res.json());
      await uploadCreativeImages(apiFetch, id, creatives, creativeIds);
      return id;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
  });
}

async function publishRequest(apiFetch: ApiFetch, id: string) {
  return z.union([MetaPublishResponseSchema, LinkedInPublishResponseSchema]).parse(
    await (
      await apiFetch(`${BASE}/requests/${encodeURIComponent(id)}/publish`, {
        method: 'POST',
        // Up to 10 creatives means a few dozen Meta writes; the server keeps going after a
        // client timeout, so the default 15 seconds would report a publish that succeeds.
        timeoutMs: 180_000,
      })
    ).json(),
  );
}

function usePublishInvalidation() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
      queryClient.invalidateQueries({ queryKey: [...META_KEY, 'campaigns'] }),
      queryClient.invalidateQueries({ queryKey: [...LINKEDIN_KEY, 'campaigns'] }),
    ]);
}

export function usePublishAdRequest(id: string) {
  const { apiFetch } = useApi();
  const onSettled = usePublishInvalidation();
  return useMutation({ mutationFn: () => publishRequest(apiFetch, id), onSettled });
}

/** Publishes a request known only once it's created or saved, for a publisher skipping review. */
export function usePublishRequestById() {
  const { apiFetch } = useApi();
  const onSettled = usePublishInvalidation();
  return useMutation({ mutationFn: (id: string) => publishRequest(apiFetch, id), onSettled });
}

export function useReviewAdRequest(id: string) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (review: { decision: 'changes_requested' | 'rejected'; note: string }) => {
      await apiFetch(`${BASE}/requests/${encodeURIComponent(id)}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(review),
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
  });
}

export function useAgentTurn() {
  const { apiFetch } = useApi();
  return useMutation({
    mutationFn: async (turn: AgentTurnRequest) =>
      AgentTurnResponseSchema.parse(
        await (
          await apiFetch(`${BASE}/agent/turn`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(turn),
            timeoutMs: 90_000,
          })
        ).json(),
      ),
  });
}

export function useUpdateAdRequest(id: string) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    // Saved creatives get their new images first, so a resubmit never puts a request back in the
    // queue with an old one. New creatives only get an id from the save; until their images land,
    // publish refuses the request.
    mutationFn: async ({
      draft,
      creatives,
      audiences,
      resubmit,
      onVideoProgress,
    }: {
      draft: MetaDraft;
      creatives: DraftCreative[];
      audiences: MetaAudience[];
      resubmit: boolean;
      onVideoProgress: (fraction: number) => void;
    }) => {
      const specs = await uploadCreativeVideos(apiFetch, creatives, onVideoProgress);
      await uploadCreativeImages(apiFetch, id, creatives, creatives.map(c => c.id));
      const update: AdRequestUpdate = { request: toSubmit(draft, specs, audiences), resubmit };
      const res = await apiFetch(`${BASE}/requests/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(update),
      });
      const { creativeIds } = AdRequestUpdatedResponseSchema.parse(await res.json());
      const added = creatives.filter(c => !c.id);
      await uploadCreativeImages(apiFetch, id, added, added.map(c => creativeIds[creatives.indexOf(c)] ?? null));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...REQUESTS_KEY] }),
  });
}

export function useCampaignAsk(platform: AdPlatform, campaignId: string) {
  const { apiFetch } = useApi();
  return useMutation({
    mutationFn: async (messages: ChatMessages) =>
      CampaignAskResponseSchema.parse(
        await (
          await apiFetch(`${BASE}/campaigns/${platform}/${encodeURIComponent(campaignId)}/ask`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messages }),
            timeoutMs: 90_000,
          })
        ).json(),
      ).reply,
  });
}

// The draft copy is only context for the review, so it's trimmed to keep the header small.
const CREATIVE_CONTEXT_CHARS = 500;

export function useCreativeCheck() {
  const { apiFetch } = useApi();
  return useMutation({
    mutationFn: async (input: {
      platform: AdPlatform;
      kind: 'image' | 'video';
      image: Blob;
      width: number;
      height: number;
      draft: { headline: string; primaryText: string };
    }) => {
      const query = new URLSearchParams({
        platform: input.platform,
        kind: input.kind,
        width: String(input.width),
        height: String(input.height),
      });
      const draft = {
        headline: input.draft.headline.slice(0, CREATIVE_CONTEXT_CHARS),
        primaryText: input.draft.primaryText.slice(0, CREATIVE_CONTEXT_CHARS),
      };
      const res = await apiFetch(`${BASE}/creative-check?${query.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': input.image.type, [CREATIVE_DRAFT_HEADER]: encodeURIComponent(JSON.stringify(draft)) },
        body: input.image,
        timeoutMs: 90_000,
      });
      return CreativeCheckResponseSchema.parse(await res.json());
    },
  });
}

export function useLinkedInStatus() {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...LINKEDIN_KEY, 'status'],
    queryFn: async () => LinkedInStatusResponseSchema.parse(await (await apiFetch(`${BASE}/linkedin/status`)).json()),
    staleTime: Infinity,
  });
}

export function useLinkedInCampaignGroups(enabled: boolean) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...LINKEDIN_KEY, 'groups'],
    queryFn: async () =>
      LinkedInCampaignGroupListResponseSchema.parse(await (await apiFetch(`${BASE}/linkedin/campaign-groups`)).json()).groups,
    enabled,
    staleTime: Infinity,
  });
}

export function useLinkedInCampaigns(enabled: boolean, groupId?: string | null) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...LINKEDIN_KEY, 'campaigns', groupId ?? 'all'],
    queryFn: async () => {
      const query = groupId ? `?groupId=${encodeURIComponent(groupId)}` : '';
      return LinkedInCampaignListResponseSchema.parse(await (await apiFetch(`${BASE}/linkedin/campaigns${query}`)).json()).campaigns;
    },
    enabled: enabled && groupId !== null,
  });
}

export function useLinkedInCampaign(id: string) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...LINKEDIN_KEY, 'campaigns', 'detail', id],
    queryFn: async () =>
      LinkedInCampaignDetailResponseSchema.parse(await (await apiFetch(`${BASE}/linkedin/campaigns/${encodeURIComponent(id)}`)).json()),
  });
}

/** Looks up a pasted post link; `url` is set when the field loses focus, not on every keystroke. */
export function useLinkedInPost(url: string) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...LINKEDIN_KEY, 'post', url],
    queryFn: async () =>
      LinkedInPostResolveResponseSchema.parse(
        await (await apiFetch(`${BASE}/linkedin/posts/resolve?url=${encodeURIComponent(url)}`)).json(),
      ),
    enabled: url.startsWith('https://'),
    staleTime: Infinity,
  });
}

export function useSubmitLinkedInRequest() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: LinkedInRequestSubmit) => {
      const res = await apiFetch(`${BASE}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
      return AdRequestCreatedResponseSchema.parse(await res.json()).id;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
  });
}

/**
 * Loads an existing ad, or every ad of an ad set or campaign, to prefill a new request. Images
 * come through our Meta media proxy, so they upload with the request like picked files; one that
 * can't be read comes back null.
 */
export function useMetaAdSearch(query: string, enabled: boolean) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...META_KEY, 'ad-search', query],
    queryFn: async () =>
      MetaAdSearchResponseSchema.parse(await (await apiFetch(`${BASE}/meta/ads/search?q=${encodeURIComponent(query)}`)).json())
        .results,
    enabled,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
}

/** What to import: a pasted Ads Manager link or id, or a campaign, ad set or ad picked from the search. */
export type MetaImportSource = { url: string } | { kind: MetaAdSearchResult['kind']; id: string };

export function useImportMetaAd() {
  const { apiFetch } = useApi();
  return useMutation({
    mutationFn: async (source: MetaImportSource) => {
      const params = new URLSearchParams('url' in source ? { url: source.url } : { kind: source.kind, id: source.id });
      const imported = MetaAdImportSchema.parse(await (await apiFetch(`${BASE}/meta/ads/import?${params.toString()}`)).json());
      // Meta's links to the same image don't all load (some are signed or served via the Page), so
      // each is tried in turn.
      const load = async (mediaUrls: string[]) => {
        for (const mediaUrl of mediaUrls) {
          try {
            return await blobToDataUrl(await (await apiFetch(`${BASE}/meta/media?url=${encodeURIComponent(mediaUrl)}`)).blob());
          } catch {
            // Try the next copy.
          }
        }
        return null;
      };
      const imageDataUrls = await Promise.all(
        imported.creatives.map(async c => ({ feed: await load(c.imageUrls), vertical: await load(c.vertical?.imageUrls ?? []) })),
      );
      return { imported, imageDataUrls };
    },
  });
}
